import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import dayjs from 'dayjs';
import { InActionListHistory } from 'app/types';
import {
  mockHistoryItemComposition,
  mockHistoryItemProgress,
  mockSourceKindLabel,
} from 'pages/InAction/mockInAction';
import IACompositionBar from 'pages/InAction/components/IACompositionBar';

interface IALibraryCardProps {
  item: InActionListHistory;
  onOpen: () => void;
  onLearn?: () => void;
  onDelete: () => void;
}

const POSITIVE = '#3B6D11';

const IALibraryCard: React.FC<IALibraryCardProps> = ({
  item,
  onOpen,
  onLearn,
  onDelete,
}) => {
  const kind = mockSourceKindLabel(item.sourceType);
  const comp = mockHistoryItemComposition(item);
  const progress = mockHistoryItemProgress(item);
  const done = progress.pct >= 100;

  return (
    <Box
      onClick={onOpen}
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'primary.tableBorder',
        borderRadius: 2,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.75,
        p: '18px 20px',
        transition: 'all .16s ease',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: '0 16px 36px -26px rgba(20,20,20,0.4)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Stack direction={'row'} alignItems={'center'} spacing={1.5}>
        <Box
          sx={{
            alignItems: 'center',
            bgcolor: (t) =>
              t.palette.mode === 'light'
                ? 'rgba(59,44,147,0.10)'
                : 'rgba(155,124,255,0.18)',
            borderRadius: 1.25,
            color: 'primary.main',
            display: 'inline-flex',
            flex: 'none',
            height: 42,
            justifyContent: 'center',
            width: 42,
          }}
        >
          {kind.icon === 'file' ? (
            <InsertDriveFileOutlinedIcon sx={{ fontSize: 22 }} />
          ) : (
            <TextSnippetOutlinedIcon sx={{ fontSize: 22 }} />
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 700,
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.title || 'Без назви'}
          </Typography>
          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: 12.5,
              mt: 0.25,
            }}
          >
            {`${kind.label} · ${item.wordsCount.toLocaleString(
              'uk-UA',
            )} слів · ${dayjs(item.createdAt).format('DD.MM.YYYY')}`}
          </Typography>
        </Box>
        {done && (
          <Stack
            direction={'row'}
            alignItems={'center'}
            spacing={0.5}
            sx={{
              color: POSITIVE,
              flex: 'none',
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 16 }} />
            <Box component={'span'}>{'Опрацьовано'}</Box>
          </Stack>
        )}
      </Stack>

      <IACompositionBar comp={comp} height={10} />

      <Stack
        direction={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}
        spacing={1.5}
      >
        <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
          <Box
            component={'b'}
            sx={{ color: 'primary.main', fontWeight: 800 }}
          >
            {comp.new}
          </Box>
          {` нових · опрацьовано ${progress.pct}%`}
        </Typography>
        <Stack direction={'row'} spacing={1}>
          {!done && onLearn && (
            <Button
              variant={'contained'}
              size={'small'}
              startIcon={<SchoolOutlinedIcon sx={{ fontSize: 18 }} />}
              onClick={(e) => {
                e.stopPropagation();
                onLearn();
              }}
              sx={{ height: 36, px: 1.5 }}
            >
              {'Вчити'}
            </Button>
          )}
          <Button
            variant={'outlined'}
            size={'small'}
            startIcon={<OpenInNewIcon sx={{ fontSize: 18 }} />}
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
            sx={{ height: 36, px: 1.5 }}
          >
            {'Відкрити'}
          </Button>
          <Button
            variant={'text'}
            size={'small'}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            sx={{
              color: 'text.secondary',
              height: 36,
              minWidth: 36,
              px: 0.75,
            }}
            aria-label={'Видалити'}
          >
            <DeleteOutlinedIcon sx={{ fontSize: 18 }} />
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default IALibraryCard;
